import React, { useState, useRef } from 'react';

const SipDelayCalculatorSection = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [investmentPeriod, setInvestmentPeriod] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [delayMonths, setDelayMonths] = useState(12);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);
  const resultRef = useRef(null);

  const calculateDelayImpact = () => {
    const monthlyReturnRate = expectedReturn / 100 / 12;
    const totalMonths = investmentPeriod * 12;
    
    // Calculate future value without delay
    const futureValueWithoutDelay = monthlyInvestment * 
      (Math.pow(1 + monthlyReturnRate, totalMonths) - 1) / monthlyReturnRate;
    
    // Calculate future value with delay (shorter investment period)
    const reducedMonths = totalMonths - delayMonths;
    let futureValueWithDelay = 0;
    if (reducedMonths > 0) {
      futureValueWithDelay = monthlyInvestment * 
        (Math.pow(1 + monthlyReturnRate, reducedMonths) - 1) / monthlyReturnRate;
    }
    
    // Calculate the cost of delay
    const costOfDelay = futureValueWithoutDelay - futureValueWithDelay;
    
    // Calculate total investments
    const totalInvestmentWithoutDelay = monthlyInvestment * totalMonths;
    const totalInvestmentWithDelay = monthlyInvestment * Math.max(0, reducedMonths);
    
    // Calculate wealth gain
    const wealthGainWithoutDelay = futureValueWithoutDelay - totalInvestmentWithoutDelay;
    const wealthGainWithDelay = futureValueWithDelay - totalInvestmentWithDelay;
    const lostWealthGain = wealthGainWithoutDelay - wealthGainWithDelay;
    
    // Calculate percentage impact
    const percentageImpact = futureValueWithoutDelay > 0 ? 
      (costOfDelay / futureValueWithoutDelay * 100) : 0;

    // Generate chart data for comparison
    const chartPoints = [];
    for (let year = 0; year <= investmentPeriod; year++) {
      const monthsElapsed = year * 12;
      
      // Without delay calculation
      const investedAmountWithoutDelay = monthlyInvestment * monthsElapsed;
      let accumulatedWithoutDelay = 0;
      if (monthsElapsed > 0) {
        accumulatedWithoutDelay = monthlyInvestment * 
          (Math.pow(1 + monthlyReturnRate, monthsElapsed) - 1) / monthlyReturnRate;
      }
      
      // With delay calculation
      const effectiveMonths = Math.max(0, monthsElapsed - delayMonths);
      const investedAmountWithDelay = monthlyInvestment * effectiveMonths;
      let accumulatedWithDelay = 0;
      if (effectiveMonths > 0) {
        accumulatedWithDelay = monthlyInvestment * 
          (Math.pow(1 + monthlyReturnRate, effectiveMonths) - 1) / monthlyReturnRate;
      }
      
      chartPoints.push({
        year,
        withoutDelay: {
          invested: investedAmountWithoutDelay,
          accumulated: accumulatedWithoutDelay
        },
        withDelay: {
          invested: investedAmountWithDelay,
          accumulated: accumulatedWithDelay
        }
      });
    }
    
    setChartData(chartPoints);
    setResult({
      futureValueWithoutDelay: Math.round(futureValueWithoutDelay),
      futureValueWithDelay: Math.round(futureValueWithDelay),
      costOfDelay: Math.round(costOfDelay),
      totalInvestmentWithoutDelay: Math.round(totalInvestmentWithoutDelay),
      totalInvestmentWithDelay: Math.round(totalInvestmentWithDelay),
      wealthGainWithoutDelay: Math.round(wealthGainWithoutDelay),
      wealthGainWithDelay: Math.round(wealthGainWithDelay),
      lostWealthGain: Math.round(lostWealthGain),
      percentageImpact: percentageImpact.toFixed(2),
      reducedMonths: Math.max(0, reducedMonths)
    });

    // Scroll to results section
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const styles = {
    sipDelayCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0',
      padding: '1rem',
      backgroundColor: '#fff7ed',
      borderRadius: '8px',
      border: '1px solid #fed7aa'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    resultSection: {
      marginTop: '3rem',
      padding: '2rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '15px',
      border: '1px solid #e5e7eb'
    },
    chartSection: {
      marginBottom: '2rem'
    },
    chartTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#1a1a1a',
      marginBottom: '1rem',
      textAlign: 'center'
    },
    chartContainer: {
      position: 'relative',
      height: '400px',
      backgroundColor: 'white',
      borderRadius: '10px',
      border: '1px solid #e5e7eb',
      padding: '20px',
      marginBottom: '2rem'
    },
    costOfDelayItem: {
      padding: '1.5rem',
      backgroundColor: '#fef2f2',
      borderRadius: '12px',
      border: '1px solid #fecaca',
      textAlign: 'center',
      marginBottom: '1.5rem'
    },
    costOfDelayLabel: {
      fontSize: '1rem',
      color: '#991b1b',
      marginBottom: '0.5rem',
      fontWeight: '600'
    },
    costOfDelayValue: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#dc2626'
    },
    comparisonSection: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1rem'
    },
    comparisonItem: {
      padding: '1.5rem',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    withoutDelayItem: {
      backgroundColor: '#f0fdf4',
      borderColor: '#bbf7d0'
    },
    withDelayItem: {
      backgroundColor: '#fef2f2',
      borderColor: '#fecaca'
    },
    comparisonTitle: {
      fontSize: '0.9rem',
      fontWeight: '600',
      marginBottom: '0.5rem'
    },
    withoutDelayTitle: {
      color: '#166534'
    },
    withDelayTitle: {
      color: '#059669'
    },
    comparisonValue: {
      fontSize: '1.1rem',
      fontWeight: '700'
    },
    withoutDelayValue: {
      color: '#059669'
    },
    withDelayValue: {
      color: '#059669'
    },
    summaryText: {
      fontSize: '1rem',
      color: '#1a1a1a',
      textAlign: 'center',
      marginTop: '1rem',
      lineHeight: '1.6',
      padding: '1.5rem',
      backgroundColor: '#e3f2fd',
      borderRadius: '12px'
    }
  };

  // SVG Bar Chart Component
  const DelayComparisonChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const finalData = data[data.length - 1];
    const chartWidth = 760;
    const chartHeight = 300;
    const padding = 60;
    const plotWidth = chartWidth - padding * 2;
    const plotHeight = chartHeight - padding * 2;

    const maxValue = Math.max(finalData.withoutDelay.accumulated, finalData.withDelay.accumulated);
    const costOfDelay = finalData.withoutDelay.accumulated - finalData.withDelay.accumulated;

    // Bar dimensions
    const barWidth = plotWidth / 4;
    const barSpacing = barWidth * 0.3;
    
    // Bar positions
    const startTodayX = padding + barSpacing;
    const delayedStartX = startTodayX + barWidth + barSpacing;
    const lossX = delayedStartX + barWidth + barSpacing;

    // Bar heights
    const startTodayHeight = (finalData.withoutDelay.accumulated / maxValue) * plotHeight;
    const delayedStartHeight = (finalData.withDelay.accumulated / maxValue) * plotHeight;
    const lossHeight = (costOfDelay / maxValue) * plotHeight;

    // Y positions (bars grow from bottom)
    const baseY = chartHeight - padding;
    const startTodayY = baseY - startTodayHeight;
    const delayedStartY = baseY - delayedStartHeight;
    const lossY = baseY - lossHeight;

    // Generate Y-axis labels
    const yLabels = [];
    for (let i = 0; i <= 5; i++) {
      const value = (maxValue / 5) * i;
      yLabels.push({
        value: Math.round(value),
        y: chartHeight - padding - (value / maxValue) * plotHeight
      });
    }

    return (
      <svg width="100%" height="400" viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white' }}>
        {/* Grid lines */}
        <defs>
          <pattern id="delay-grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width={chartWidth} height={chartHeight} fill="url(#delay-grid)" />
        
        {/* Y-axis labels */}
        {yLabels.map((label, i) => (
          <g key={i}>
            <line x1={padding} y1={label.y} x2={chartWidth - padding} y2={label.y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padding - 10} y={label.y + 5} textAnchor="end" fontSize="12" fill="#6b7280">
              {(label.value / 1000).toFixed(0)}K
            </text>
          </g>
        ))}
        
        {/* Bars */}
        {/* Start Today Bar */}
        <rect 
          x={startTodayX} 
          y={startTodayY} 
          width={barWidth} 
          height={startTodayHeight}
          fill="#8b5cf6"
          stroke="#8b5cf6"
          strokeWidth="1"
        />
        
        {/* Delayed Start Bar */}
        <rect 
          x={delayedStartX} 
          y={delayedStartY} 
          width={barWidth} 
          height={delayedStartHeight}
          fill="#84cc16"
          stroke="#84cc16"
          strokeWidth="1"
        />
        
        {/* Loss Bar */}
        <rect 
          x={lossX} 
          y={lossY} 
          width={barWidth} 
          height={lossHeight}
          fill="#059669"
          stroke="#059669"
          strokeWidth="1"
        />
        
        {/* Value labels on bars */}
        <text 
          x={startTodayX + barWidth/2} 
          y={startTodayY - 10} 
          textAnchor="middle" 
          fontSize="11" 
          fill="#374151"
          fontWeight="600"
        >
          Amount Accumulated Rs.{(finalData.withoutDelay.accumulated / 1000).toFixed(0)}K
        </text>
        
        <text 
          x={delayedStartX + barWidth/2} 
          y={delayedStartY - 10} 
          textAnchor="middle" 
          fontSize="11" 
          fill="#374151"
          fontWeight="600"
        >
          Amount Accumulated Rs.{(finalData.withDelay.accumulated / 1000).toFixed(0)}K
        </text>
        
        <text 
          x={lossX + barWidth/2} 
          y={lossY - 10} 
          textAnchor="middle" 
          fontSize="11" 
          fill="#374151"
          fontWeight="600"
        >
          Notional Loss Rs.{(costOfDelay / 1000).toFixed(0)}K
        </text>
        
        {/* X-axis labels */}
        <text 
          x={startTodayX + barWidth/2} 
          y={chartHeight - padding + 20} 
          textAnchor="middle" 
          fontSize="12" 
          fill="#374151"
          fontWeight="600"
        >
          Start Today
        </text>
        
        <text 
          x={delayedStartX + barWidth/2} 
          y={chartHeight - padding + 20} 
          textAnchor="middle" 
          fontSize="12" 
          fill="#374151"
          fontWeight="600"
        >
          Delayed Start
        </text>
        
        <text 
          x={lossX + barWidth/2} 
          y={chartHeight - padding + 20} 
          textAnchor="middle" 
          fontSize="12" 
          fill="#374151"
          fontWeight="600"
        >
          Loss
        </text>
        
        {/* Axis labels */}
        <text x={25} y={chartHeight / 2} textAnchor="middle" fontSize="14" fontWeight="600" fill="#374151" transform={`rotate(-90, 25, ${chartHeight / 2})`}>
          Amount (Rs.)
        </text>
      </svg>
    );
  };

  return (
    <>
      <style>{`
        .sip-delay-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .sip-delay-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .sip-delay-calculate-btn:hover {
          background-color: #059669 !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .sip-delay-result-grid {
            grid-template-columns: 1fr !important;
            gap: 0.5rem !important;
          }
          .sip-delay-comparison-grid {
            grid-template-columns: 1fr !important;
            gap: 0.5rem !important;
          }
        }
      `}</style>
      
      <div style={styles.sipDelayCalculatorContent}>
        <div style={styles.description}>
          This calculator illustrates how much you stand to lose in case you delay the starting of your SIP by even a few months!
        </div>
        
        {/* Monthly Investment */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Monthly Investment (Rs.)
            <input
              type="number"
              min="2000"
              max="1000000"
              step="1000"
              value={monthlyInvestment}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setMonthlyInvestment('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setMonthlyInvestment(Math.max(2000, Math.min(1000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setMonthlyInvestment(25000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="2000"
              max="1000000"
              step="1000"
              value={monthlyInvestment}
              onChange={(e) => setMonthlyInvestment(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-delay-slider"
            />
            <div style={styles.sliderLabels}>
              <span>2,000</span>
              <span>251,500</span>
              <span>501,000</span>
              <span>750,500</span>
              <span>1,000,000</span>
            </div>
          </div>
        </div>

        {/* Investment Period */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Investment period (in years)
            <input
              type="number"
              min="1"
              max="30"
              step="1"
              value={investmentPeriod}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setInvestmentPeriod('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setInvestmentPeriod(Math.max(1, Math.min(30, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setInvestmentPeriod(10);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter years"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="30"
              step="1"
              value={investmentPeriod}
              onChange={(e) => setInvestmentPeriod(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-delay-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>4</span>
              <span>7</span>
              <span>10</span>
              <span>13</span>
              <span>16</span>
              <span>19</span>
              <span>21</span>
              <span>24</span>
              <span>27</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Expected Rate of Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected rate of return (% p.a)
            <input
              type="number"
              min="1"
              max="30"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExpectedReturn('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setExpectedReturn(Math.max(1, Math.min(30, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setExpectedReturn(12);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="30"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="sip-delay-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>3.9</span>
              <span>6.8</span>
              <span>9.7</span>
              <span>12.6</span>
              <span>15.5</span>
              <span>18.4</span>
              <span>21.3</span>
              <span>24.2</span>
              <span>27.1</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Delay in Starting SIP */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Delay in Starting SIP (in months)
            <input
              type="number"
              min="1"
              max="120"
              step="1"
              value={delayMonths}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setDelayMonths('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setDelayMonths(Math.max(1, Math.min(120, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setDelayMonths(12);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter months"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="120"
              step="1"
              value={delayMonths}
              onChange={(e) => setDelayMonths(parseInt(e.target.value))}
              style={styles.slider}
              className="sip-delay-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>13</span>
              <span>25</span>
              <span>37</span>
              <span>49</span>
              <span>62</span>
              <span>74</span>
              <span>86</span>
              <span>98</span>
              <span>108</span>
              <span>120</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="sip-delay-calculate-btn"
          onClick={calculateDelayImpact}
        >
          Calculate
        </button>

        {/* Results */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            {/* Delay Comparison Chart */}
            <div style={styles.chartSection}>
              <h3 style={styles.chartTitle}>SIP - Cost of Delay</h3>
              <DelayComparisonChart data={chartData} />
            </div>

            {/* Result Summary */}
            <div style={styles.summaryText}>
              Delay of <strong>{delayMonths} months</strong> in starting your SIP will cause a notional 
              Loss of: <strong>Rs. {result.costOfDelay.toLocaleString()}</strong> in the final value of your investment.
            </div>

            <div style={styles.resultNote}>
              Note: Based on monthly compounding
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SipDelayCalculatorSection;